import React from "react";
import { SummaryFigures } from "../types";
import { fmt } from "../utils/format";
import { equationText } from "../utils/reportSections";

/**
 * Print markup for one summary block — the 4 boxes, the equation, Detailed
 * Income (with Partner Deposits as its own group) and Detailed Expenses.
 * Shared by the Financial Report and Monthly Brief print layouts; styled by
 * utils/printStyles.
 */
const PrintSummaryBlock: React.FC<{ figures: SummaryFigures }> = ({ figures: f }) => {
  const positive = f.balanceOnHand >= 0;

  return (
    <>
      <section className="pr-summary">
        <div className="pr-box pr-slate">
          <span>Carry Forward</span>
          <strong>{fmt(f.carryForward)}</strong>
        </div>
        <div className="pr-box pr-green">
          <span>Total Income</span>
          <strong>+ {fmt(f.incomeTotal)}</strong>
        </div>
        <div className="pr-box pr-red">
          <span>Total Expenses</span>
          <strong>- {fmt(f.expenseTotal)}</strong>
        </div>
        <div className={`pr-box ${positive ? "pr-green" : "pr-red"}`}>
          <span>Balance on Hand</span>
          <strong>{fmt(f.balanceOnHand)}</strong>
        </div>
      </section>
      <p className="pr-equation">{equationText(f)}</p>

      <section className="pr-section">
        <h3>Detailed Income</h3>
        <table className="pr-table pr-green-head">
          <thead>
            <tr>
              <th>Income Source</th>
              <th className="pr-num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {f.incomeBreakdown.length + f.depositBreakdown.length === 0 && (
              <tr>
                <td colSpan={2}>No income in this period.</td>
              </tr>
            )}
            {f.incomeBreakdown.map((r) => (
              <tr key={`i${r.id}`}>
                <td>{r.name}</td>
                <td className="pr-num">{fmt(r.amount)}</td>
              </tr>
            ))}
            {f.depositBreakdown.length > 0 && (
              <tr className="pr-subhead">
                <td colSpan={2}>Partner Deposits</td>
              </tr>
            )}
            {f.depositBreakdown.map((r) => (
              <tr key={`d${r.id}`}>
                <td>{r.name}</td>
                <td className="pr-num">{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total Income</td>
              <td className="pr-num">{fmt(f.incomeTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </section>

      <section className="pr-section">
        <h3>Detailed Expenses</h3>
        <table className="pr-table pr-red-head">
          <thead>
            <tr>
              <th>Expense Category</th>
              <th className="pr-num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {f.expenseBreakdown.length === 0 && (
              <tr>
                <td colSpan={2}>No expenses in this period.</td>
              </tr>
            )}
            {f.expenseBreakdown.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td className="pr-num">{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total Expenses</td>
              <td className="pr-num">{fmt(f.expenseTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </section>
    </>
  );
};

export default PrintSummaryBlock;
